import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import moment from 'moment';
import IMAGES from '../../../../constant/images';

/**
 * BookingCardMobile Component
 */
const BookingCardMobile = ({ booking, onCancel, onReschedule, onJoinNow, onRebook, onViewDocuments, currentTime }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [canJoinNow, setCanJoinNow] = useState(false);

  // Appointment status constants
  const APPOINTMENT_STATUS = {
    SCHEDULED: "SCHEDULED",
    IN_PROGRESS: "IN_PROGRESS",
    PENDING: "PENDING",
    COMPLETED: "COMPLETED",
    IN_COMPLETE: "IN_COMPLETE",
    EXPIRED: "EXPIRED",
    CANCELED: "CANCELED",
    COUNSELOR_UNAVAILABLE: "COUNSELOR_UNAVAILABLE",
  };

  // Calculate join window and time left
  useEffect(() => {
    if (
      booking.status === APPOINTMENT_STATUS.SCHEDULED &&
      booking.date &&
      booking.startTime &&
      booking.endTime
    ) {
      const now = currentTime || moment();
      const start = moment(`${booking.date} ${booking.startTime}`, 'YYYY-MM-DD HH:mm:ss');
      const end = moment(`${booking.date} ${booking.endTime}`, 'YYYY-MM-DD HH:mm:ss');
      const minStartTime = start.clone().subtract(5, 'minutes');

      // Check if can join 
      const canJoin = now.isBetween(minStartTime, end);
      setCanJoinNow(canJoin);

      if (!canJoin && now.isBefore(start)) {
        // Calculate time until appointment
        const duration = moment.duration(start.diff(now));
        const minutes = Math.floor(duration.asMinutes());

        if (minutes < 60) {
          setTimeLeft(`${minutes}min left`);
        } else {
          const hours = Math.floor(duration.asHours());
          const mins = minutes % 60;
          setTimeLeft(mins > 0 ? `${hours}h ${mins}min left` : `${hours}h left`);
        }
      }
    }
  }, [booking, currentTime]);

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    return moment(date).format('MMM D, YYYY');
  };

  // Format time range
  const formatTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return '';
    const start = moment(startTime, 'HH:mm:ss').format('h:mm a');
    const end = moment(endTime, 'HH:mm:ss').format('h:mm a');
    return `${start} - ${end}`;
  };

  // Get mode display
  const getModeDisplay = (mode) => {
    switch (mode) {
      case 'VIDEO':
        return { label: 'Online', icon: IMAGES.video_icon };
      case 'AUDIO':
        return { label: 'Online', icon: IMAGES.audio_call };
      case 'CHAT':
        return { label: 'Online', icon: IMAGES.chat_icon };
      case 'INPERSON':
      case 'IN_PERSON':
        return { label: 'In-Person', icon: IMAGES.in_person };
      default:
        return { label: 'Online', icon: IMAGES.video_icon };
    }
  };

  // Get effective status
  const getEffectiveStatus = () => {
    if (booking.status === APPOINTMENT_STATUS.SCHEDULED && booking.currentStatus) {
      return booking.currentStatus;
    }
    return booking.status;
  };

  // Get status badge
  const getStatusBadge = () => {
    const effectiveStatus = getEffectiveStatus();

    // Handle Missed statuses
    if (effectiveStatus.includes('MISSED')) {
      return {
        label: effectiveStatus.includes('STUDENT') ? 'Missed by You' : effectiveStatus.includes('COUNSELLOR') ? 'Missed by Counsellor' : 'Missed',
        dotColor: 'bg-[#EC3234]'
      };
    }

    // Handle regular status values
    switch (effectiveStatus) {
      case APPOINTMENT_STATUS.SCHEDULED:
        return { label: 'Confirmed', dotColor: 'bg-[#138808]' };
      case APPOINTMENT_STATUS.COMPLETED:
        return { label: 'Completed', dotColor: 'bg-[#138808]' };
      case APPOINTMENT_STATUS.CANCELED:
        return { label: 'Cancelled', dotColor: 'bg-[#EC3234]' };
      case APPOINTMENT_STATUS.COUNSELOR_UNAVAILABLE:
        return { label: 'Practitioner Unavailable', dotColor: 'bg-[#FF7439]' };
      default:
        return { label: 'Incomplete', dotColor: 'bg-[#FFAF77]' };
    }
  };

  const modeDisplay = getModeDisplay(booking.mode);
  const statusBadge = getStatusBadge();
  const isPastOrCancelled =
    booking.status === APPOINTMENT_STATUS.CANCELED ||
    booking.status === APPOINTMENT_STATUS.COUNSELOR_UNAVAILABLE ||
    booking.status === APPOINTMENT_STATUS.EXPIRED;

  // Check if appointment is missed
  const isMissedAppointment = () => {
    const effectiveStatus = getEffectiveStatus();
    return effectiveStatus.includes('MISSED');
  };

  // Check if appointment is truly upcoming
  const isUpcomingAppointment = () => {
    const now = currentTime || moment();
    const endMoment = moment(`${booking.date} ${booking.endTime}`, 'YYYY-MM-DD HH:mm:ss');
    const isFuture = endMoment.isAfter(now);

    return (
      [APPOINTMENT_STATUS.SCHEDULED, APPOINTMENT_STATUS.IN_PROGRESS, APPOINTMENT_STATUS.PENDING].includes(booking.status) &&
      isFuture &&
      !isMissedAppointment()
    );
  };

  return (
    <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden font-poppins">
      {/* Header: Booking ID, Mode, Status */}
      <div className="bg-[#E7EDF5] px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-1 text-xs leading-4">
          <span className="text-[#64748B] font-medium">Booking ID</span>
          <span className="text-[#171717] font-semibold">{booking.appointmentId || 'N/A'}</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Mode Badge */}
          <div className="bg-white px-2 py-0.5 rounded-full flex items-center gap-1">
            {modeDisplay.icon && (
              <img src={modeDisplay.icon} alt={modeDisplay.label} className="w-[14px] h-[14px] object-contain" />
            )}
            <span className="text-[11px] leading-4 text-[#2563EB] font-semibold">{modeDisplay.label}</span>
          </div>

          {/* Status Badge */}
          <div className="bg-white px-1 py-0.5 rounded-full flex items-center gap-1">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${statusBadge.dotColor === 'bg-[#138808]' ? 'bg-white' : 'bg-white'}`}>
              <div className={`w-2.5 h-2.5 rounded-full ${statusBadge.dotColor}`}></div>
            </div>
            <span className="text-[11px] text-[#4B61FF] font-semibold pr-1">{statusBadge.label}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 flex flex-col gap-3">
        {/* Practitioner Info */}
        <div className="flex gap-4">
          {/* Image */}
          <div className="w-20 h-20 rounded-xl border-[1px] border-solid border-[#E2E8F0] overflow-hidden flex-shrink-0">
            {booking.counsellorImageURL ? (
              <img
                src={booking.counsellorImageURL}
                alt={`${booking.counsellorFirstName} ${booking.counsellorLastName}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#F8FAFC] flex items-center justify-center">
                <img src={IMAGES.user_filled} alt="user" className="w-10 h-10 object-contain" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col gap-3 flex-1">
            {/* Name and Rating */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <h3 className="text-base leading-normal font-semibold text-[#171717] mb-0">
                  {booking.counsellorTitle || 'Dr.'} {booking.counsellorFirstName} {booking.counsellorLastName}
                </h3>
                {booking.counsellorRating && (
                  <div className="flex items-center gap-1">
                    <Star size={16} className="fill-[#F59E0B] text-[#F59E0B]" />
                    <span className="text-xs leading-4 text-[#334155]">
                      {booking.counsellorRating}
                    </span>
                  </div>
                )}
              </div>

              {/* Specialization */}
              {booking.counsellorSpecialization && (
                <div className="flex items-start gap-1">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0 mt-0.5">
                    <circle cx="8" cy="8" r="6" stroke="#2563EB" strokeWidth="1.2" fill="none"/>
                  </svg>
                  <span className="text-xs text-[#64748B] leading-normal flex-1">
                    {booking.counsellorSpecialization}
                  </span>
                </div>
              )}
            </div>

            {/* Rescheduled Badge */}
            {booking.isRescheduled && (
              <div className="bg-[#FFF7ED] px-2.5 py-0.5 rounded-full inline-block self-start">
                <span className="text-[11px] leading-4 text-[#EA580C] font-semibold">Rescheduled</span>
              </div>
            )}

            {/* Date and Time */}
            <div className="flex gap-4">
              {/* Date */}
              <div className="flex items-center gap-2">
                <img
                  src={IMAGES.calender_icon}
                  alt="calendar"
                  className="w-5 h-5 object-contain flex-shrink-0"
                />
                <span className="text-xs leading-4 text-[#334155] font-medium">
                  {formatDate(booking.date)}
                </span>
              </div>

              {/* Time */}
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="7.5" stroke="#2563EB" strokeWidth="1.5"/>
                    <path d="M10 5v5l3 2" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="text-xs leading-4 text-[#334155] font-medium">
                  {formatTimeRange(booking.startTime, booking.endTime)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="h-px bg-[#F1F5F9]"></div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3">
          {/* Show different actions based on status */}
          {canJoinNow && booking.status === APPOINTMENT_STATUS.SCHEDULED && (
            <>
              <div className="bg-white px-1 py-1 rounded-lg">
                <span className="text-xs leading-4 text-[#4B61FF] font-semibold">{timeLeft}</span>
              </div>
              <button
                onClick={() => onJoinNow(booking)}
                className="btn-gradient border-0 px-3 rounded-lg h-8 flex items-center justify-center min-w-[80px]"
              >
                <span className="text-xs leading-normal text-white font-semibold">Join Now</span>
              </button>
            </>
          )}

          {!canJoinNow && isUpcomingAppointment() && (
            <>
              <button
                onClick={() => onCancel(booking)}
                className="bg-white border-[1px] border-solid border-[#E2E8F0] shadow-sm hover:bg-[#F8FAFC] transition px-3 rounded-lg h-8 flex items-center justify-center"
              >
                <span className="text-xs leading-4 text-[#171717] font-semibold">Cancel</span>
              </button>
              <button
                onClick={() => onReschedule(booking)}
                className="btn-gradient border-0 pl-3 pr-1.5 rounded-lg h-8 flex items-center gap-1"
              >
                <span className="text-xs leading-4 text-white font-semibold">Re-schedule</span>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className="flex-shrink-0">
                  <path d="M7.5 5l5 5-5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </>
          )}

          

          {/* Re-book button for Cancelled and Missed appointments */}
          {(booking.status === APPOINTMENT_STATUS.CANCELED ||
            booking.status === APPOINTMENT_STATUS.COUNSELOR_UNAVAILABLE ||
            isMissedAppointment()) && (
            <div className="flex items-center justify-between w-full">
              {booking.status === APPOINTMENT_STATUS.CANCELED && (
                <div className="bg-[#FEF2F2] px-2.5 py-0.5 rounded-full">
                  <span className="text-[11px] leading-4 text-[#DC2626] font-semibold">You cancelled this appointment.</span>
                </div>
              )}
              <button
                onClick={() => onRebook(booking)}
                className="bg-white border-[1px] border-solid border-[#E2E8F0] shadow-sm hover:bg-[#F8FAFC] transition px-3 rounded-lg h-8 flex items-center justify-center"
              >
                <span className="text-xs leading-4 text-[#171717] font-semibold">Re-Book</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingCardMobile;
